import { useCallback, useState } from "react";
import { useErrorBoundary } from "react-error-boundary";

import { getExhClient, isExhClientInitialized } from "lib/extrahorizon/client";
import { hospitalConfigToFbcSettingsData, hospitalConfigToWbcSettingsData } from "lib/extrahorizon/settings/converters";
import { extractValidationErrors } from "lib/extrahorizon/utils";
import { ExhFbcSettingsData, ExhWbcSettingsData } from "types/measurement";

import { HospitalConfiguration, HospitalRegion } from "../context/types";

export type UpdateSettingsResult =
  | { status: "success" }
  | { status: "validation_error"; errors: Record<string, string> }
  | { status: "missing_document" };

interface UseUpdateExhSettingsDocumentResult {
  updateSettings: (
    region: HospitalRegion,
    documentId: string | undefined,
    config: HospitalConfiguration
  ) => Promise<UpdateSettingsResult | null>;
  loading: boolean;
  error: Error | null;
}

/**
 * Hook for updating hospital settings documents via the ExtraHorizon SDK.
 * Targets `measurement-wbc-settings` (USA) or `measurement-fbc-settings` (UK).
 * If no document exists for the hospital, returns `missing_document` so the
 * caller can surface an error — hospitals are expected to be seeded with a
 * settings document at provisioning time.
 */
export const useUpdateExhSettingsDocument = (): UseUpdateExhSettingsDocumentResult => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { showBoundary: onError } = useErrorBoundary();

  const updateSettings = useCallback(
    async (
      region: HospitalRegion,
      documentId: string | undefined,
      config: HospitalConfiguration
    ): Promise<UpdateSettingsResult | null> => {
      if (!isExhClientInitialized()) {
        const err = new Error("ExtraHorizon client not initialized");
        setError(err);
        onError(err);
        return null;
      }

      if (!documentId) {
        return { status: "missing_document" };
      }

      const schemaName = region === "USA" ? "measurement-wbc-settings" : "measurement-fbc-settings";
      const data: ExhWbcSettingsData | ExhFbcSettingsData =
        region === "USA" ? hospitalConfigToWbcSettingsData(config) : hospitalConfigToFbcSettingsData(config);

      try {
        setLoading(true);
        setError(null);
        const client = getExhClient();
        await client.data.documents.update(schemaName, documentId, data);
        return { status: "success" };
      } catch (err) {
        const validationErrors = extractValidationErrors(err);
        if (validationErrors) {
          return { status: "validation_error", errors: validationErrors };
        }
        const e = err instanceof Error ? err : new Error("Failed to update settings document");
        setError(e);
        onError(e);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [onError]
  );

  return { updateSettings, loading, error };
};
